import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import SidePanel from 'components/SidePanel/SidePanel';
import { getAIAgentsSidebarAgentOptions } from 'constants/general';

import type IAIAgentsSidebar from './interfaces/IAIAgentsSidebar';

import styles from './sass/AIAgentsSidebar.module.scss';

function AIAgentsSidebar({
  open,
  onClose,
  onSelectSupplierNTier,
  onSelectReputationMonitoring,
  onSelectRegulationMonitoring,
  onSelectFinanceMonitoring,
  onSelectOwnershipMonitoring,
  onSelectClimateMonitoring,
  hasSupplierNTierAttention,
  hasReputationMonitoringAttention,
  hasRegulationMonitoringAttention,
  hasFinanceMonitoringAttention,
  hasOwnershipMonitoringAttention,
  hasClimateMonitoringAttention,
}: IAIAgentsSidebar): React.JSX.Element {
  const { t } = useTranslation();

  const agentOptions = useMemo(
    () => getAIAgentsSidebarAgentOptions({
      onSelectSupplierNTier,
      onSelectReputationMonitoring,
      onSelectRegulationMonitoring,
      onSelectFinanceMonitoring,
      onSelectOwnershipMonitoring,
      onSelectClimateMonitoring,
      hasSupplierNTierAttention,
      hasReputationMonitoringAttention,
      hasRegulationMonitoringAttention,
      hasFinanceMonitoringAttention,
      hasOwnershipMonitoringAttention,
      hasClimateMonitoringAttention,
    }),
    [
      hasClimateMonitoringAttention,
      hasFinanceMonitoringAttention,
      hasOwnershipMonitoringAttention,
      hasRegulationMonitoringAttention,
      hasReputationMonitoringAttention,
      hasSupplierNTierAttention,
      onSelectClimateMonitoring,
      onSelectFinanceMonitoring,
      onSelectOwnershipMonitoring,
      onSelectRegulationMonitoring,
      onSelectReputationMonitoring,
      onSelectSupplierNTier,
    ],
  );

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role="dialog"
      ariaLabel={t('valueChainMap.aiAgentsSidebarTitle')}
    >
      <div className={styles.header}>
        <span className={styles.headerTitle}>{t('valueChainMap.aiAgentsSidebarTitle')}</span>
      </div>

      <div className={styles.content}>
        <div className={styles.agentList}>
          {agentOptions.map((agentOption) => {
            const {
              id,
              titleKey,
              descriptionKey,
              Icon,
              disabled,
              onClick,
              showComingSoon,
              showAttentionBadge,
            } = agentOption;

            return (
              <button
                key={id}
                type="button"
                className={clsx(styles.agentCard, {
                  [styles.agentCardDisabled]: disabled,
                  [styles.agentCardInteractive]: !disabled,
                })}
                onClick={onClick}
                disabled={disabled}
                aria-label={t(titleKey)}
                data-i18n-key={titleKey}
              >
                {showComingSoon ? (
                  <span className={styles.comingSoonBadge}>
                    {t('valueChainMap.aiAgentsSidebarComingSoon')}
                  </span>
                ) : null}
                {showAttentionBadge ? <span className={styles.attentionBadge} aria-hidden="true" /> : null}

                <div className={styles.agentCardHeader}>
                  <div
                    className={clsx(styles.iconWrapper, {
                      [styles.iconWrapperDisabled]: disabled,
                    })}
                  >
                    <Icon className={clsx(styles.agentIcon, { [styles.iconWrapperDisabled]: disabled })} />
                  </div>
                  <span className={styles.agentTitle}>{t(titleKey)}</span>
                </div>
                <p className={styles.agentDescription}>{t(descriptionKey)}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          data-i18n-key="common.close"
        >
          {t('common.close')}
        </button>
      </div>
    </SidePanel>
  );
}

export default AIAgentsSidebar;
